import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import Pagination from '../../components/Pagination/Pagination';
import { useCurrency } from '../../context/CurrencyContext';
import './PaymentHistoryPage.css';

const EARNING_COLORS = {
  subscription: '#1F396C',
  badge: '#006928',
  promotion: '#691C00',
  ads: '#691C00',
  membership: '#1F396C'
};

const PaymentHistoryPage = () => {
  const { currencies } = useCurrency();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [gatewayFilter, setGatewayFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState({ text: '', type: '' });
  const [fetchError, setFetchError] = useState('');
  const exportRef = useRef(null);
  const itemsPerPage = 10;

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setFetchError('Not authenticated. Please log in again.');
        setPayments([]);
        return;
      }
      const params = new URLSearchParams({ page: currentPage, limit: itemsPerPage });
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      if (gatewayFilter) params.append('gateway', gatewayFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const res = await axios.get(`${API_URL}/payment-gateway/admin/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(res.data.transactions || []);
      setTotalPages(res.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching payments:', error);
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        setFetchError('Session expired or unauthorized. Please log out and log in again.');
      } else {
        setFetchError('Failed to load payments. Please try again.');
      }
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter, gatewayFilter, startDate, endDate]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleSelectAll = () => {
    if (selectedItems.length === payments.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(payments.map(p => p.id));
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Close export dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = async (format) => {
    setShowExportDropdown(false);
    setExporting(true);
    setExportMessage({ text: '', type: '' });
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ format });
      if (selectedItems.length > 0) {
        params.append('ids', selectedItems.join(','));
      }
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      if (gatewayFilter) params.append('gateway', gatewayFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const res = await axios.get(`${API_URL}/payment-gateway/admin/transactions/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
      a.href = url;
      a.download = `payments_export_${dateStr}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setExportMessage({ text: `Exported ${selectedItems.length > 0 ? selectedItems.length : 'all'} payment(s) as ${format.toUpperCase()}`, type: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      setExportMessage({ text: 'Export failed. Please try again.', type: 'error' });
    } finally {
      setExporting(false);
      setTimeout(() => setExportMessage({ text: '', type: '' }), 4000);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      success: { color: '#006928', bg: '#E8F5E9' },
      pending: { color: '#E65100', bg: '#FFF3E0' },
      failed: { color: '#B71C1C', bg: '#FFEBEE' }
    };
    const s = styles[status] || styles.pending;
    return <span style={{ color: s.color, background: s.bg, padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500 }}>{status}</span>;
  };

  return (
    <AdminLayout>
      <div className="admin-payment-history-content">
        <div className="aph-page-header">
          <div className="aph-page-title-section">
            <h1 className="aph-page-title">Payment History</h1>
            <p className="aph-page-description">View and manage all payment transactions across the platform</p>
          </div>
          <div className="aph-header-actions">
            <div className="aph-export-wrapper" ref={exportRef}>
              <button
                className={`aph-bulk-btn aph-bulk-export${selectedItems.length === 0 ? ' aph-export-disabled' : ''}`}
                onClick={() => selectedItems.length > 0 && setShowExportDropdown(!showExportDropdown)}
                disabled={exporting}
                title={selectedItems.length === 0 ? 'Select payments to export' : `Export ${selectedItems.length} selected`}
              >
                {exporting ? 'Exporting...' : `Export (${selectedItems.length})`}
              </button>
              {showExportDropdown && (
                <div className="aph-export-dropdown">
                  <button className="aph-export-option" onClick={() => handleExport('csv')}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 1h5.5L13 4.5V13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2z" stroke="#006928" strokeWidth="1.2"/><path d="M9 1v4h4" stroke="#006928" strokeWidth="1.2"/></svg>
                    Export as CSV
                  </button>
                  <button className="aph-export-option" onClick={() => handleExport('xlsx')}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 1h5.5L13 4.5V13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2z" stroke="#1F396C" strokeWidth="1.2"/><path d="M9 1v4h4" stroke="#1F396C" strokeWidth="1.2"/></svg>
                    Export as Excel
                  </button>
                </div>
              )}
            </div>
            {exportMessage.text && (
              <span className={`aph-export-message aph-export-${exportMessage.type}`}>{exportMessage.text}</span>
            )}
          </div>
        </div>

        <div className="aph-list-header">
          <div className="aph-list-controls-left">
            <div className="aph-select-wrapper">
              <select className="aph-filter-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">All Status</option>
                <option value="success">Success</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="aph-select-wrapper">
              <select className="aph-filter-select" value={gatewayFilter} onChange={(e) => { setGatewayFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">All Gateways</option>
                <option value="razorpay">Razorpay</option>
                <option value="paypal">PayPal</option>
                <option value="stripe">Stripe</option>
                <option value="paytm">Paytm</option>
              </select>
            </div>
          </div>
          <div className="aph-list-controls-right">
            <div className="aph-search-box">
              <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }} />
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="9.16667" cy="9.16667" r="5.83333" stroke="#969696" strokeWidth="1.5" />
                <path d="M14.166 14.1665L17.4993 17.4998" stroke="#969696" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            <div className="aph-date-group">
              <div className="aph-date-picker">
                <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }} />
              </div>
              <span className="aph-date-separator">-</span>
              <div className="aph-date-picker">
                <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }} />
              </div>
            </div>
          </div>
        </div>

        <div className="aph-content">
          {loading ? (
            <div className="aph-loading">Loading payment history...</div>
          ) : (
            <div className="aph-table">
              <div className="aph-table-header">
                <div className="aph-th aph-th-checkbox">
                  <input type="checkbox" checked={payments.length > 0 && selectedItems.length === payments.length} onChange={handleSelectAll} />
                  <span>All</span>
                </div>
                <div className="aph-th">Date</div>
                <div className="aph-th">Company</div>
                <div className="aph-th">Email</div>
                <div className="aph-th">Gateway</div>
                <div className="aph-th">Amount</div>
                <div className="aph-th">Purpose</div>
                <div className="aph-th">Status</div>
              </div>

              <div className="aph-table-body">
                {payments.length > 0 ? (
                  payments.map((payment) => (
                    <div key={payment.id} className="aph-table-row">
                      <div className="aph-td aph-td-checkbox">
                        <input type="checkbox" checked={selectedItems.includes(payment.id)} onChange={() => handleSelectItem(payment.id)} />
                      </div>
                      <div className="aph-td aph-td-date">{formatDate(payment.createdAt)}</div>
                      <div className="aph-td aph-td-name" title={payment.user?.companyName || '-'}>{payment.user?.companyName || payment.user?.firstName || '-'}</div>
                      <div className="aph-td aph-td-email" title={payment.user?.email}>{payment.user?.email || '-'}</div>
                      <div className="aph-td aph-td-location" style={{ textTransform: 'capitalize' }}>{payment.gateway}</div>
                      <div className="aph-td aph-td-amount">
                        <span className="aph-amount-text">{(currencies.find(c => c.code === payment.currency)?.symbol || payment.currency + ' ')}{payment.amount?.toFixed(2)}</span>
                        <span className="aph-plan-badge">{payment.currency}</span>
                      </div>
                      <div className="aph-td aph-td-earning">
                        <span className="aph-earning-badge" style={{ color: EARNING_COLORS[payment.purpose] || '#333' }}>
                          {payment.purpose || '-'}
                        </span>
                      </div>
                      <div className="aph-td">{getStatusBadge(payment.status)}</div>
                    </div>
                  ))
                ) : (
                  <div className="aph-no-data">
                    {fetchError ? (
                      <div style={{ color: '#B71C1C' }}>{fetchError}</div>
                    ) : (
                      'No payment records found'
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>
    </AdminLayout>
  );
};

export default PaymentHistoryPage;
